// app.component.ts
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MediaMatcher } from '@angular/cdk/layout';
import {
  Router,
  NavigationEnd,
  Event as RouterEvent,
} from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DeviceService } from './shared/services/client/device.service';
import { SocketService } from './shared/services/client/socket.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Official';
  mobileQuery: MediaQueryList;

  // Parent page info (now reactive)
  parentUrl$ = new BehaviorSubject<string | null>(null);
  parentOrigin$ = new BehaviorSubject<string | null>(null);

  private subscriptions = new Subscription();
  private _mobileQueryListener: () => void;
  private _messageListener: (event: MessageEvent) => void;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private media: MediaMatcher,
    private router: Router,
    private deviceService: DeviceService,
    private socketService: SocketService
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);

    this._messageListener = (event: MessageEvent) => this.handlePostMessage(event);
  }

  ngOnInit(): void {
    this.setupParentUrlDetection();
    this.setupSocketParentUrlSync();
    this.setupScrollToTopOnNavigation();
  }

  // NEW: Scroll to top on every successful route change
  private setupScrollToTopOnNavigation(): void {
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
        .subscribe(() => {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth' // Smooth scroll for better UX
          });
        })
    );
  }

  private setupParentUrlDetection(): void {
    if (this.isInIframe()) {
      // 1. Try direct same-origin access
      try {
        const url = window.parent.location.href;
        const origin = window.parent.location.origin;
        this.setParentInfo(url, origin);
        return;
      } catch (e) {
        console.info('Cross-origin iframe – using fallbacks');
      }

      // 2. Fallback to referrer
      if (document.referrer) {
        try {
          const referrer = new URL(document.referrer);
          this.setParentInfo(referrer.href, referrer.origin);
          return;
        } catch (e) {
          console.warn('Invalid referrer URL:', document.referrer);
        }
      }

      // 3. Request via postMessage (cross-origin)
      this.deviceService.oInfoNotification('Info', 'Requesting parent page URL...');
      window.parent.postMessage({ type: 'REQUEST_PARENT_URL' }, '*');

      window.addEventListener('message', this._messageListener);

      // Timeout fallback
      setTimeout(() => {
        if (!this.parentUrl$.value) {
          this.deviceService.oErrorNotification(
            'Warning',
            'Parent page did not respond with URL after 10s'
          );
        }
      }, 10000);
    } else {
      console.info('App not running in iframe – parent detection skipped');
    }
  }

  private handlePostMessage(event: MessageEvent): void {
    if (!event.data || typeof event.data !== 'object') return;
    if (event.data.source === 'react-devtools-content-script') return;

    if (event.data.type === 'PARENT_URL_RESPONSE' && event.data.url) {
      try {
        const urlObj = new URL(event.data.url);
        this.setParentInfo(urlObj.href, urlObj.origin);

        // Acknowledge receipt
        if (event.source) {
          (event.source as WindowProxy).postMessage(
            { type: 'PARENT_URL_ACK' },
            event.origin
          );
        }
      } catch (e) {
        console.warn('Invalid parent URL received:', event.data.url);
        this.deviceService.oErrorNotification('Error', 'Invalid parent URL format');
      }
    }
  }

  private setParentInfo(url: string, origin: string): void {
    this.parentUrl$.next(url);
    this.parentOrigin$.next(origin);

    console.log('Parent URL set:', url);
    this.deviceService.oSuccessNotification(
      'Success',
      `Connected to parent: ${new URL(url).hostname}`
    );

    // Immediate send if socket is ready
    if (this.socketService.isConnected) {
      this.sendParentInfoToBackend(url, origin);
    }
  }

  private sendParentInfoToBackend(url: string, origin: string): void {
    this.socketService.socket.emit('parent-page-info', {
      parentUrl: url,
      parentOrigin: origin,
      timestamp: Date.now(),
      socketId: this.socketService.socketId$.value,
    });
  }

  private setupSocketParentUrlSync(): void {
    // Send parent info whenever socket connects (handles reconnects)
    this.subscriptions.add(
      this.socketService.isConnected$.subscribe((connected) => {
        if (connected && this.parentUrl$.value && this.parentOrigin$.value) {
          this.sendParentInfoToBackend(this.parentUrl$.value, this.parentOrigin$.value);
        }
      })
    );
  }

  private isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true; // safer default for cross-origin
    }
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    window.removeEventListener('message', this._messageListener);
    this.subscriptions.unsubscribe();
  }
}